import { useEffect, useRef, useState } from "react";
import type { Terminal } from "@xterm/xterm";
import type { BuildLogLine } from "@/client/director/buildLogs.tsx";

// A light terminal theme so Bazel's ANSI colors read on the console's light
// background; the cursor is hidden (matched to the background) since the view is
// read-only.
const xtermTheme = {
	background: "#f8fafc",
	foreground: "#1e293b",
	cursor: "#f8fafc",
	selectionBackground: "#cbd5e1",
	black: "#334155",
	red: "#dc2626",
	green: "#16a34a",
	yellow: "#b45309",
	blue: "#2563eb",
	magenta: "#9333ea",
	cyan: "#0891b2",
	white: "#475569",
	brightBlack: "#64748b",
	brightRed: "#ef4444",
	brightGreen: "#16a34a",
	brightYellow: "#ca8a04",
	brightBlue: "#3b82f6",
	brightMagenta: "#a855f7",
	brightCyan: "#06b6d4",
	brightWhite: "#1e293b",
};

// stripAnsi removes ANSI color and cursor-control escape sequences. Bazel emits
// colorized, curses-redrawn progress (it assumes a TTY); rendered as plain text
// those become noise like "\x1b[1A\x1b[K". xcode output has none, so this is a
// no-op there.
const ansiEscape = /\x1b\[[0-9;?]*[a-zA-Z]/g;
export function stripAnsi(s: string): string {
	return s.replace(ansiEscape, "");
}

// colorize tints an xcode log line by event type to match the pre-xterm renderer
// (command=cyan, stderr=red); xcode output carries no ANSI of its own, so this is
// what makes its terminal view readable. The reset keeps the color from bleeding to
// the next line. Terminal display only: the clipboard/download path strips, and
// Persisted "$ " lines are also tagged as commands and colored here.
function colorize(type: string, data: string): string {
	const color = type === "command" ? "\x1b[36m" : type === "stderr" ? "\x1b[31m" : "";
	if (!color) return data;
	const ending = data.endsWith("\r\n") ? "\r\n" : data.endsWith("\n") ? "\n" : "";
	const body = ending ? data.slice(0, -ending.length) : data;
	return `${color}${body}\x1b[0m${ending}`;
}

// joinChunks reconstructs log text from live typed events or persisted raw-line
// chunks. strip removes ANSI for clipboard/download. Live xcode events need
// inserted newlines; persisted and Bazel chunks already carry their delimiters.
export function joinChunks(lines: BuildLogLine[], perLine: boolean, strip: boolean): string {
	return lines
		.filter((l) => l.type !== "meta" && typeof l.data === "string")
		.map((l) => {
			const data = l.data as string;
			if (strip) return stripAnsi(data);
			return perLine || l.type === "command" ? colorize(l.type, data) : data;
		})
		.join(perLine ? "\n" : "");
}

// trimTrailingResidue drops Bazel's residual status line from the end of a finished
// log: it keeps a transient "INFO:" line at the bottom that the tty erases on exit,
// but that erase never reaches the BES stream. Done at the text level (not per
// chunk) because Bazel may batch "Build completed\nINFO:\n" into one event, so a
// per-chunk trim would see real content and keep the residue. Only the literal
// "INFO:" is matched (not a general [A-Z]+: class), so a real trailing status label
// like "FAILED:" or "PASSED:" from a test or genrule is never mistaken for residue.
const trailingResidue = /^INFO:$/;
function isResidue(row: string): boolean {
	const v = stripAnsi(row).trim();
	return v === "" || trailingResidue.test(v);
}
function trimTrailingResidue(text: string): string {
	const rows = text.split("\n");
	let end = rows.length;
	while (end > 0 && isResidue(rows[end - 1])) end--;
	return rows.slice(0, end).join("\n");
}

// XtermLogPanel renders a log through a real terminal emulator (xterm.js,
// lazy-loaded) so curses progress and ANSI color render exactly as they do
// in the user's terminal, instead of being flattened to noisy plain text. While the
// source runs it appends new chunks (tailing in place); once finished, its content is
// stable, so it rewrites the terminal once from the full, residue-trimmed text. Keyed
// by source id by the caller so each selection gets a fresh terminal. perLine is for
// line-per-event sources (live xcode, session app logs); Bazel chunks already carry
// their newlines.
export function XtermLogPanel({
	lines,
	perLine,
	finished,
}: {
	lines: BuildLogLine[];
	perLine?: boolean;
	finished?: boolean;
}) {
	const containerRef = useRef<HTMLDivElement>(null);
	const termRef = useRef<Terminal | null>(null);
	const writtenRef = useRef(0);
	const finalizedRef = useRef(false);
	const [ready, setReady] = useState(false);

	useEffect(() => {
		let disposed = false;
		let term: Terminal | undefined;
		let ro: ResizeObserver | undefined;
		void (async () => {
			const [{ Terminal }, { FitAddon }] = await Promise.all([
				import("@xterm/xterm"),
				import("@xterm/addon-fit"),
				import("@xterm/xterm/css/xterm.css"),
			]);
			if (disposed || !containerRef.current) return;
			term = new Terminal({
				disableStdin: true,
				cursorBlink: false,
				// Bazel writes bare \n assuming a TTY translates it to \r\n (ONLCR);
				// convertEol replicates that so lines start at column 0 instead of
				// staircasing, and its curses progress redraws correctly.
				convertEol: true,
				fontSize: 12,
				fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
				scrollback: 20000,
				theme: xtermTheme,
			});
			const fit = new FitAddon();
			term.loadAddon(fit);
			term.open(containerRef.current);
			try {
				fit.fit();
			} catch {
				// Container not laid out yet; the ResizeObserver re-fits once it is.
			}
			termRef.current = term;
			writtenRef.current = 0;
			ro = new ResizeObserver(() => {
				try {
					fit.fit();
				} catch {
					// Hidden (inactive tab); refit happens when shown.
				}
			});
			ro.observe(containerRef.current);
			setReady(true);
		})();
		return () => {
			disposed = true;
			ro?.disconnect();
			term?.dispose();
			termRef.current = null;
		};
	}, []);

	useEffect(() => {
		if (!ready) return;
		const term = termRef.current;
		if (!term) return;
		// Finished source: content is stable. Rewrite once from the full, residue-
		// trimmed text (this also clears any transient status line written while it
		// streamed live). finalizedRef guards against re-running on SWR revalidation.
		if (finished) {
			if (finalizedRef.current) return;
			finalizedRef.current = true;
			term.reset();
			term.write(trimTrailingResidue(joinChunks(lines, !!perLine, false)));
			term.scrollToBottom();
			return;
		}
		// A shrinking source means the caller cleared it (e.g. the live
		// app-log view switching apps); start the terminal over instead of
		// appending after stale content.
		if (lines.length < writtenRef.current) {
			term.reset();
			writtenRef.current = 0;
		}
		const buf = term.buffer.active;
		const wasAtBottom = buf.viewportY >= buf.baseY;
		for (let i = writtenRef.current; i < lines.length; i++) {
			const l = lines[i];
			if (l.type !== "meta" && typeof l.data === "string") {
				term.write(perLine ? colorize(l.type, l.data) + "\n" : l.data);
			}
		}
		writtenRef.current = lines.length;
		if (wasAtBottom) term.scrollToBottom();
	}, [ready, lines, finished, perLine]);

	return (
		<div className="min-h-0 flex-1 rounded-b-lg bg-[#f8fafc] p-2">
			<div ref={containerRef} className="h-full w-full" />
		</div>
	);
}
